import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, ProgressBar, Button, Modal } from 'react-bootstrap';
import { useParams, useSearchParams } from 'react-router-dom';
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import Thumbnails from "yet-another-react-lightbox/plugins/thumbnails";
import "yet-another-react-lightbox/plugins/thumbnails.css";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import Fullscreen from "yet-another-react-lightbox/plugins/fullscreen";
import Slideshow from "yet-another-react-lightbox/plugins/slideshow";
import Captions from "yet-another-react-lightbox/plugins/captions";
import Video from "yet-another-react-lightbox/plugins/video";
import './SingleCampaign.css';
import SampleImage from '../../Assets/Images/Donation.webp';
import CTAPrimaryButton from '../../Components/CTAButton/CTAPrimaryButton/CTAPrimaryButton';
import { useNavigate } from 'react-router-dom';
import NotFoundImage from '../../Assets/Images/NotFound.svg';
import { CampaignService } from '../../Services/Axios/Api';
import { t } from 'i18next';
import i18n from '../../Services/i18n/i18n';
import { ARCURRENCY } from '../../Constants';
import { toast } from 'react-toastify';
import { faQrcode } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { QRCodeSVG } from 'qrcode.react';
import { getTenantFavicon, TENANT_FAVICON_GLOBAL_KEY } from '../../Services/bootstrap/tenantBoot';

const SingleCampaign = () => {
    const navigate = useNavigate();
    const { name } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const id = searchParams.get('id');
    const [campaign, setCampaign] = useState(null);
    const [loading, setLoading] = useState(true);
    const [index, setIndex] = useState(-1);
    const [slides, setSlides] = useState([]);
    const [mediaUrls, setMediaUrls] = useState([]);
    const [showQuickDonate, setShowQuickDonate] = useState(false);
    const quickDonateQrRef = useRef(null);
    // Mock campaign data - in real app this would come from an API
    const mockCampaigns = [
        {
            id: '1',
            title: 'Education Fund',
            description: 'Support education for underprivileged children in rural areas. Your donation will help provide school supplies, uniforms, and educational materials to children who cannot afford them.',
            type: 'Normal Campaign',
            mainPhoto: SampleImage,
            mediaFiles: [
                { type: 'image', src: SampleImage, title: 'Classroom Setup', description: 'Students in their new classroom' },
                { type: 'image', src: SampleImage, title: 'Students Learning', description: 'Students engaged in learning activities' },
                { type: 'image', src: SampleImage, title: 'School Supplies', description: 'New school supplies for students' }
            ]
        },
        {
            id: '2',
            title: 'Medical Emergency',
            description: 'Help cover medical expenses for families in need. Your contribution will go towards life-saving treatments, medications, and hospital bills for those who cannot afford healthcare.',
            type: 'Progressing Campaign',
            mainPhoto: SampleImage,
            current: 25000,
            target: 50000,
            mediaFiles: [
                { type: 'image', src: SampleImage, title: 'Medical Equipment', description: 'Modern medical equipment in use' },
                { type: 'image', src: SampleImage, title: 'Patient Care', description: 'Healthcare professionals providing care' },
                { type: 'image', src: SampleImage, title: 'Hospital Facility', description: 'State-of-the-art hospital facilities' }
            ]
        }
    ];

    useEffect(() => {
        const fetchCampaign = async () => {
            try {
                const response = await CampaignService.getCampaignById(id);
                setCampaign(response.result);
                const imageUrls = response.result?.additionalImages?.split(',') || [];
                setMediaUrls(imageUrls);
                console.log("imageUrls", imageUrls);
                // Utility function to check for video file extensions
                const isVideoFile = (src) => /\.(mp4|mov|webm|avi|mkv|m4v|ogg|wmv)$/i.test(src);
                // Utility function to check for image file extensions
                const isImageFile = (src) => /\.(jpg|jpeg|png|gif|bmp|webp|svg|heic|heif|tif|tiff)$/i.test(src);
                // Utility function to get MIME type from extension
                const getVideoMimeType = (src) => {
                    const ext = src.split('.').pop().toLowerCase();
                    if (ext === 'mp4') return 'video/mp4';
                    if (ext === 'webm') return 'video/webm';
                    if (ext === 'ogg') return 'video/ogg';
                    if (ext === 'mov') return 'video/mp4';
                    if (ext === 'avi') return 'video/x-msvideo';
                    if (ext === 'mkv') return 'video/x-matroska';
                    if (ext === 'm4v') return 'video/x-m4v';
                    if (ext === 'wmv') return 'video/x-ms-wmv';
                    return 'video/mp4';
                };
                // Find a poster image for videos (mainImage or first image)
                const poster = response.result?.mainImage || (imageUrls.find(url => !isVideoFile(url)) || '');
                // Build lightbox slides only for images and videos; embeds will render inline only
                setSlides(imageUrls
                    .filter((src) => isVideoFile(src) || isImageFile(src))
                    .map((src, i) => (
                        isVideoFile(src)
                            ? {
                                type: 'video',
                                sources: [{ src, type: getVideoMimeType(src) }],
                                poster: poster,
                                title: `Video ${i + 1}`,
                                description: `Gallery video ${i + 1}`
                            }
                            : {
                                src: src,
                                type: 'image',
                                title: `Image ${i + 1}`,
                                description: `Gallery image ${i + 1}`
                            }
                    ))
                );
                console.log("slides", slides);
            } catch (error) {
                console.error('Error fetching campaign:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchCampaign();
    }, [id]);

    const handleDownloadQuickDonateQr = () => {
        const svgEl = quickDonateQrRef.current;
        if (!svgEl) return;

        const serialized = new XMLSerializer().serializeToString(svgEl);
        const svgWithDoctype = `<?xml version="1.0" encoding="UTF-8"?>\n${serialized}`;
        const blob = new Blob([svgWithDoctype], { type: "image/svg+xml;charset=utf-8" });
        const url = URL.createObjectURL(blob);

        const a = document.createElement("a");
        a.href = url;
        a.download = `quick-donate-qr-${campaign?.id || "campaign"}.svg`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    };

    // Update Meta Tags for SEO and Link Previews
    useEffect(() => {
        if (!campaign) return;

        // Store original title to revert later if needed (optional)
        const originalTitle = document.title;
        document.title = campaign.name || 'Abwab Elkheir';

        // Helper to update or create meta tags
        const updateMeta = (property, content) => {
            let element = document.querySelector(`meta[property="${property}"]`);
            if (!element) {
                element = document.createElement('meta');
                element.setAttribute('property', property);
                document.head.appendChild(element);
            }
            element.setAttribute('content', content);
        };

        const cleanUrl = `${window.location.origin}/campaigns/c?id=${campaign.id}`;

        updateMeta('og:title', campaign.name);
        updateMeta('og:description', campaign.description);
        updateMeta('og:image', campaign.mainImage);
        updateMeta('og:url', cleanUrl);
        updateMeta('og:type', 'website');

        return () => {
            document.title = originalTitle;
            // Optionally clean up meta tags, but leaving them is usually fine for SPA navigation
        };
    }, [campaign]);

    const handleShare = async () => {
        // Construct URL with Arabic slug for better user visibility
        // Regex replaces spaces with dashes to make it look like a nice slug
        const slug = campaign.name.replace(/\s+/g, '-');
        const shareUrl = `${window.location.origin}/campaigns/${slug}?id=${campaign.id}`;

        const shareData = {
            title: campaign?.name || 'Campaign',
            text: campaign?.description || 'Check out this campaign',
            url: shareUrl
        };

        try {
            if (navigator.share) {
                await navigator.share(shareData);
            } else {
                await navigator.clipboard.writeText(shareUrl);
                toast.success(t('LinkCopied') || 'Link copied to clipboard');
            }
        } catch (error) {
            console.error('Error sharing:', error);
            try {
                await navigator.clipboard.writeText(shareUrl);
                toast.success(t('LinkCopied') || 'Link copied to clipboard');
            } catch (clipboardError) {
                console.error('Clipboard error:', clipboardError);
            }
        }
    };

    const getCampaignSlug = () => (campaign?.name || '').replace(/\s+/g, '-');

    const getShareUrl = () => {
        const slug = getCampaignSlug();
        return `${window.location.origin}/campaigns/${slug}?id=${campaign.id}`;
    };

    const getQuickDonateUrl = () => {
        const slug = getCampaignSlug();
        return `${window.location.origin}/donate/${slug}?id=${campaign.id}`;
    };

    const handleCopyQuickDonateLink = async () => {
        try {
            await navigator.clipboard.writeText(getQuickDonateUrl());
            toast.success(t('LinkCopied') || 'Link copied to clipboard');
        } catch (error) {
            console.error('Clipboard error:', error);
        }
    };

    const safeParseOptions = (optionsString) => {
        if (!optionsString || typeof optionsString !== 'string') return {};
        try {
            const parsed = JSON.parse(optionsString);
            return parsed && typeof parsed === 'object' ? parsed : {};
        } catch {
            return {};
        }
    };

    if (loading) {
        return (
            <Container className="mt-5 text-center">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </Container>
        );
    }

    if (!campaign) {
        return (
            <Container className="mt-5 mb-5 text-center">
                <div className="col-12 Center mt-3 mb-3">
                    <img src={NotFoundImage} width="200px" alt="Campaign not found" />
                </div>
                <h2>Campaign not found</h2>
                <p>The campaign you're looking for doesn't exist or has been removed.</p>
            </Container>
        );
    }

    const progressPercentage = campaign.campaignSection.mode === 'Progressing'
        ? (campaign?.currentAmount && campaign?.currentAmount > 0
            ? (parseFloat(campaign.currentAmount) / parseFloat(campaign.targetAmount)) * 100
            : 0)
        : 0;
    console.log("progressPercentage", progressPercentage);

    return (
        <Container className="container mt-5">
            <Row className={slides.length > 0 ? "" : "Center"}>
                <Col
                    lg={slides && slides.length > 0 ? 8 : 10}
                    className="mb-4"
                >
                    <div className="campaign-header">
                        <h1 className="campaign-title">{campaign.name}</h1>
                        {/* <p className="campaign-type">{campaign.campaignSection.name} | {campaign.campaignSection.mode}</p> */}
                    </div>

                    <div className="campaign-main-photo mb-4">
                        <img
                            src={campaign?.mainImage}
                            alt={campaign?.title}
                            className="img-fluid rounded"
                        />
                    </div>

                    {campaign.campaignSection.mode === 'Progressing' && (
                        <div className="campaign-progress mb-4">
                            <div className="d-flex justify-content-between mb-2">
                                <span>{t('Progress')}</span>
                                <span> {campaign?.currentAmount?.toLocaleString() || 0} / {campaign.targetAmount?.toLocaleString() || 0} {ARCURRENCY}</span>
                            </div>
                            <ProgressBar
                                dir={"ltr"}
                                now={progressPercentage}
                                label={`${Math.round(progressPercentage)}%`}
                                className="campaign-progress-bar"
                            />
                        </div>
                    )}

                    <div className="campaign-description mb-4">
                        {/* <h3>{t('AboutCampaign')}</h3> */}
                        <p>{campaign.description}</p>
                    </div>

                    <div className="campaign-actions d-flex justify-content-between gap-2">
                        <CTAPrimaryButton
                            text={t('DonateNow')}
                            onClick={() => {
                                navigate(`/donate/${campaign.name}?id=${campaign.id}`);
                            }}
                            className="w-100"
                        />
                        {safeParseOptions(campaign?.options)?.showQr && (
                            <Button
                                variant="outline-secondary"
                                size="lg"
                                onClick={() => setShowQuickDonate(true)}
                                title={t('QrCode') || 'Qr Code'}
                            >
                                <FontAwesomeIcon icon={faQrcode} /> {t('QrCode') || 'Qr Code'}
                            </Button>
                        )}
                        <Button
                            variant="outline-secondary"
                            size="lg"
                            onClick={handleShare}
                            title={t('Share') || 'Share'}
                        >
                            <i className="fas fa-share-alt"></i> {t('Share') || 'Share'}
                        </Button>
                    </div>
                </Col>

                {slides && slides.length > 0 && (
                    <Col lg={4}>
                        <div className="campaign-media">
                            <h3 className="media-section-title">{t('CampaignGallery')}</h3>
                            <div className="media-grid">
                                {mediaUrls.map((src, idx) => {
                                    const isVideo = /\.(mp4|mov|webm|avi|mkv|m4v|ogg|wmv)$/i.test(src);
                                    const isImage = /\.(jpg|jpeg|png|gif|bmp|webp|svg|heic|heif|tif|tiff)$/i.test(src);
                                    // Map the grid index to lightbox slide index for image/video only
                                    const slideIndex = isVideo || isImage
                                        ? slides.findIndex((s) => (s.type === 'video' && isVideo && s.sources[0].src === src) || (s.type === 'image' && isImage && s.src === src))
                                        : -1;

                                    return (
                                        <div
                                            key={idx}
                                            className="media-item"
                                            onClick={() => { if (slideIndex >= 0) setIndex(slideIndex); }}
                                            style={{ cursor: (slideIndex >= 0 ? 'pointer' : 'default') }}
                                        >
                                            {isImage ? (
                                                <>
                                                    <img
                                                        src={src}
                                                        alt={`Media ${idx + 1}`}
                                                        className="img-fluid"
                                                    />
                                                    <div className="media-overlay">
                                                        <span className="media-title">{`Media ${idx + 1}`}</span>
                                                    </div>
                                                </>

                                            ) : isVideo ? (
                                                <>
                                                    <video
                                                        src={src}
                                                        className="img-fluid"
                                                        controls
                                                        poster={campaign?.mainImage}
                                                    />
                                                    <div className="media-overlay">
                                                        <span className="media-title">{`Media ${idx + 1}`}</span>
                                                    </div>
                                                </>

                                            ) : (
                                                <iframe
                                                    src={src}
                                                    className="img-fluid"
                                                    style={{ width: '100%', aspectRatio: '16/9', border: 'none', height: '100%' }}
                                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                                    allowFullScreen
                                                    loading="lazy"
                                                    referrerPolicy="strict-origin-when-cross-origin"
                                                    title={`campaign-embed-${idx}`}
                                                />
                                            )}

                                        </div>
                                    );
                                })}
                            </div>
                        </div>


                        <div className="col-12 Center mt-3 mb-3">
                            <CTAPrimaryButton
                                text={t('DonateNow')}
                                onClick={() => {
                                    navigate(`/donate/${campaign.name}?id=${campaign.id}`);
                                }}
                                className="w-100"
                            />
                        </div>

                    </Col>
                )}
            </Row>

            <Lightbox
                slides={slides}
                open={index >= 0}
                index={index}
                close={() => setIndex(-1)}
                plugins={[Thumbnails, Zoom, Fullscreen, Slideshow, Captions, Video]}
                carousel={{
                    finite: true
                }}
                thumbnails={{
                    position: 'bottom',
                    width: 120,
                    height: 80,
                    border: 0,
                    borderRadius: 4,
                    padding: 4,
                    gap: 16,
                    showToggle: true
                }}
                zoom={{
                    maxZoomPixelRatio: 2,
                    zoomInMultiplier: 2,
                    doubleTapDelay: 300,
                    doubleClickDelay: 300,
                    doubleClickMaxStops: 2,
                    keyboardMoveDistance: 50,
                    wheelZoomDistanceFactor: 100,
                    pinchZoomDistanceFactor: 100,
                    scrollToZoom: false
                }}
                styles={{
                    container: { backgroundColor: "rgba(0, 0, 0, .9)" },
                    root: { "--yarl__color_backdrop": "rgba(0, 0, 0, .9)" }
                }}
            />

            <Modal show={showQuickDonate} onHide={() => setShowQuickDonate(false)} centered>
                <Modal.Header closeButton>
                    {/* <Modal.Title><FontAwesomeIcon icon={faQrcode} /> {t('QrCode') || 'Qr Code'}</Modal.Title> */}
                </Modal.Header>
                <Modal.Body>
                    <div className="d-flex flex-column align-items-center gap-3">
                        {/* <img
                            alt="Qr Code"
                            width={220}
                            height={220}
                            style={{ background: 'white', padding: 8, borderRadius: 8 }}
                            src={`https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(getQuickDonateUrl())}`}
                        /> */}
                        <QRCodeSVG
                            ref={quickDonateQrRef}
                            value={getQuickDonateUrl()}
                            size={220}
                            bgColor="#ffffff"
                            fgColor="#000000"
                            level="H"
                            imageSettings={{
                                src:getTenantFavicon() || '',
                                height: 40,
                                width: 40,
                                excavate: true      
                              }} 
                        />
                        <div className="text-center small text-muted" dir="ltr" style={{ wordBreak: 'break-word' }}>
                            {getQuickDonateUrl()}
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-primary" onClick={handleDownloadQuickDonateQr}>
                        {t('Download') || 'Download'}
                    </Button>
                    <Button variant="outline-secondary" onClick={handleCopyQuickDonateLink}>
                        {t('CopyLink') || 'Copy link'}
                    </Button>
                    <Button variant="primary" onClick={() => window.open(getQuickDonateUrl(), '_blank', 'noopener,noreferrer')}>
                        {t('Open') || 'Open'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default SingleCampaign;

